import { HTTP_URL } from "../config/backend";
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = HTTP_URL;

const PLAN_EMOJI = { starter: "🎮", party: "🎉", team: "🏆" };

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const orderId        = searchParams.get("order_id");

  const [order,   setOrder]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [copied,  setCopied]  = useState(false);
  const [polls,   setPolls]   = useState(0);

  useEffect(() => {
    if (!orderId) { setError("Заказ не найден."); setLoading(false); return; }
    fetchOrder();
  }, [orderId]);

  // Опрашиваем каждые 2 сек, пока ЮKassa/webhook не подтвердили succeeded.
  useEffect(() => {
    if (!order || order.status === "succeeded") return;
    if (polls >= 15) { setError("Платёж обрабатывается дольше обычного. Обнови страницу через минуту."); return; }
    const t = setTimeout(() => { setPolls(p => p + 1); fetchOrder(); }, 2000);
    return () => clearTimeout(t);
  }, [order, polls]);

  // A paid user should not have to copy the code and activate it manually in
  // the same browser. The game catalogue already reads these two keys.
  useEffect(() => {
    if (order?.status !== "succeeded" || !order?.promo_code) return;
    localStorage.setItem("neon_promo_code", order.promo_code);
    localStorage.setItem("neon_promo_rooms", String(order.rooms ?? ""));
  }, [order?.status, order?.promo_code, order?.rooms]);

  async function fetchOrder() {
    try {
      const r = await axios.get(`${API}/api/payments/order/${orderId}`);
      setOrder(r.data);
    } catch {
      setError("Не удалось загрузить информацию о заказе.");
    } finally {
      setLoading(false);
    }
  }

  function copyCode() {
    if (!order?.promo_code) return;
    navigator.clipboard.writeText(order.promo_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // ── LOADING ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-cartoon stars-bg flex items-center justify-center">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>⏳</div>
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.3rem", color: "#9b5de5" }}>
            Проверяем оплату...
          </p>
        </div>
      </div>
    );
  }

  // ── ERROR ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-cartoon stars-bg flex items-center justify-center p-4">
        <div className="card-cartoon" style={{ maxWidth: "420px", textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>😕</div>
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.2rem", color: "#ff3355", marginBottom: "1rem" }}>{error}</p>
          <button className="btn-cartoon btn-primary px-6 py-2" onClick={() => navigate("/pro")}>
            ← Вернуться
          </button>
        </div>
      </div>
    );
  }

  // ── PENDING ───────────────────────────────────────────────────────────────
  if (order?.status !== "succeeded") {
    return (
      <div className="min-h-screen bg-cartoon stars-bg flex items-center justify-center p-4">
        <div className="card-cartoon" style={{ maxWidth: "420px", textAlign: "center" }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔄</div>
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1.2rem", color: "#ffe600", marginBottom: "0.5rem" }}>
            Платёж обрабатывается...
          </p>
          <p style={{ fontFamily: "'Nunito', sans-serif", color: "#888", fontSize: "0.85rem" }}>
            Это займёт несколько секунд
          </p>
        </div>
      </div>
    );
  }

  // ── SUCCESS ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-cartoon stars-bg flex items-center justify-center p-4">
      <div style={{ width: "100%", maxWidth: "480px" }}>

        {/* Заголовок */}
        <div className="card-cartoon" style={{ textAlign: "center", marginBottom: "1rem" }}>
          <div style={{ fontSize: "4rem", marginBottom: "0.5rem" }}>🎉</div>
          <h1 style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "2rem", color: "#3dffa0", marginBottom: "0.25rem" }}>
            Оплата прошла!
          </h1>
          <p style={{ fontFamily: "'Nunito', sans-serif", color: "#888", fontSize: "0.9rem" }}>
            Тариф «{order?.plan_name}» · {order?.amount} ₽ · {order?.rooms} игровых сессий
          </p>
        </div>

        {/* Промокод */}
        <div className="card-cartoon" style={{ textAlign: "center", marginBottom: "1rem" }}>
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1rem", color: "#9b5de5", marginBottom: "0.75rem" }}>
            🔑 Твой промокод:
          </p>

          <div style={{
            background: "#0d0820",
            border: "3px solid #9b5de5",
            borderRadius: "1rem",
            padding: "1rem 1.5rem",
            marginBottom: "1rem",
            boxShadow: "0 0 20px #9b5de544",
          }}>
            <span style={{
              fontFamily: "'Fredoka One', sans-serif",
              fontSize: "1.8rem",
              color: "#fff",
              letterSpacing: "0.1em",
            }}>
              {order?.promo_code}
            </span>
          </div>

          <button
            className={`btn-cartoon px-6 py-2 ${copied ? "btn-success" : "btn-primary"}`}
            onClick={copyCode}
            style={{ fontSize: "1rem" }}
          >
            {copied ? "✅ Скопировано!" : "📋 Скопировать код"}
          </button>

          <p style={{ fontFamily: "'Nunito', sans-serif", color: "#555", fontSize: "0.8rem", marginTop: "0.75rem" }}>
            Сохрани промокод — он даёт {order?.rooms} игровых сессий PRO
          </p>

          {/* Письмо на почту */}
          <div style={{
            background: "#1a1030", border: "1.5px solid #9b5de533",
            borderRadius: "0.75rem", padding: "0.75rem 1rem", marginTop: "0.75rem",
            display: "flex", gap: "0.6rem", alignItems: "flex-start",
          }}>
            <span style={{ fontSize: "1.1rem", flexShrink: 0 }}>📧</span>
            <p style={{ fontFamily: "'Nunito', sans-serif", color: "#aaa", fontSize: "0.78rem", margin: 0, lineHeight: 1.5 }}>
              Промокод также отправлен на твою почту.{" "}
              <span style={{ color: "#ffe600" }}>Не нашёл письмо? Проверь папку «Спам»</span>{" "}
              — иногда письма попадают туда автоматически.
            </p>
          </div>
        </div>

        {/* Инструкция */}
        <div className="card-cartoon" style={{ marginBottom: "1rem" }}>
          <p style={{ fontFamily: "'Fredoka One', sans-serif", fontSize: "1rem", color: "#ffe600", marginBottom: "0.75rem" }}>
            Как использовать:
          </p>
          {[
            "PRO уже активирован в этом браузере",
            "Открой NeonParty и выбери любую PRO игру",
            "Промокод также сохрани — он пригодится на другом устройстве",
          ].map((step, i) => (
            <div key={i} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start", marginBottom: "0.5rem" }}>
              <div style={{
                background: "#9b5de5", color: "#fff",
                borderRadius: "50%", width: "1.5rem", height: "1.5rem",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontFamily: "'Fredoka One', sans-serif", fontSize: "0.85rem", flexShrink: 0,
              }}>
                {i + 1}
              </div>
              <p style={{ fontFamily: "'Nunito', sans-serif", color: "#ccc", fontSize: "0.88rem", margin: 0, paddingTop: "0.1rem" }}>
                {step}
              </p>
            </div>
          ))}
        </div>

        {/* Кнопки */}
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button
            className="btn-cartoon btn-success flex-1 py-3"
            onClick={() => navigate("/games")}
            style={{ fontSize: "1rem" }}
          >
            🎮 Играть сейчас
          </button>
          <button
            className="btn-cartoon btn-secondary flex-1 py-3"
            onClick={() => navigate("/pro")}
            style={{ fontSize: "1rem" }}
          >
            🛒 Купить ещё
          </button>
        </div>

      </div>
    </div>
  );
}